import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * reCAPTCHA **v2 checkbox** — the "I'm not a robot" tickbox.
 *
 * Login.cshtml rendered `<div class="g-recaptcha" data-sitekey="...">` and
 * loaded `api.js` with no `render` parameter, and login.js read the token with
 * `grecaptcha.getResponse()`. That is v2, not v3: there is a visible widget the
 * user must tick, and no `action` is ever sent. Do not "upgrade" this to v3
 * without changing the server's verification call to match.
 *
 * The widget is rendered explicitly (`render=explicit`) rather than by
 * auto-scan, because the auto-scan only runs once at script load and would miss
 * the div on every client-side navigation into this page.
 *
 * The site key comes from the BFF; the Razor view interpolated it from
 * ConfigHelper.GetGoogleReCaptchaSiteKey(). It is public either way.
 */
declare global {
  interface Window {
    grecaptcha?: {
      render: (element: HTMLElement, options: { sitekey: string }) => number
      getResponse: (widgetId?: number) => string
      reset: (widgetId?: number) => void
    }
    onRecaptchaLoad?: () => void
  }
}

export interface RecaptchaHandle {
  /** '' when the user has not ticked the box — the check login.js made. */
  getResponse: () => string
  reset: () => void
}

const SCRIPT_ID = 'recaptcha-api'

/** Resolves once grecaptcha is ready; shared by every mount of the widget. */
let readyPromise: Promise<void> | null = null

function loadRecaptcha(): Promise<void> {
  if (readyPromise) return readyPromise

  readyPromise = new Promise<void>((resolve) => {
    if (window.grecaptcha?.render) {
      resolve()
      return
    }
    window.onRecaptchaLoad = () => resolve()

    if (document.getElementById(SCRIPT_ID)) return

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit'
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  })

  return readyPromise
}

export const Recaptcha = forwardRef<RecaptchaHandle>(function Recaptcha(_props, ref): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)

  const { data } = useQuery({
    queryKey: ['account', 'config'],
    queryFn: () => api.get<{ recaptchaSiteKey: string }>('/api/ecareer/account/config'),
    staleTime: Infinity,
  })
  const siteKey = data?.recaptchaSiteKey

  useEffect(() => {
    if (!siteKey) return
    let cancelled = false

    void loadRecaptcha().then(() => {
      if (cancelled || !containerRef.current || widgetIdRef.current !== null) return
      // Guard against React 18 StrictMode's double-invoke, which would
      // otherwise render two widgets into the same node and throw.
      if (containerRef.current.childElementCount > 0) return
      widgetIdRef.current = window.grecaptcha?.render(containerRef.current, { sitekey: siteKey }) ?? null
    })

    return () => {
      cancelled = true
    }
  }, [siteKey])

  useImperativeHandle(ref, () => ({
    getResponse: () =>
      widgetIdRef.current === null ? '' : (window.grecaptcha?.getResponse(widgetIdRef.current) ?? ''),
    reset: () => {
      if (widgetIdRef.current !== null) window.grecaptcha?.reset(widgetIdRef.current)
    },
  }))

  return <div ref={containerRef} className="g-recaptcha" />
})
